import type { LineItemSpecSnapshot, QuotationLineItemDto } from '../types/quotation.types';

/**
 * CSC-307: shared predicate for "is this row editable / does it require match
 * + qty + weight to be valid for submit?". Imported by both the line-items
 * table (for quick-select derivations) and submit-time guards. SUMMARY_ONLY
 * and EXTERNAL_ATTACHMENT lines are passed straight through to NetSuite as-is
 * and bypass these checks.
 */
export function isEditableRow(item: Pick<QuotationLineItemDto, 'matchStatus'>): boolean {
  return item.matchStatus !== 'SUMMARY_ONLY' && item.matchStatus !== 'EXTERNAL_ATTACHMENT';
}

/**
 * 14ykpqtr22u — the rows that actually post to the NetSuite Estimate: editable piece rows with a
 * quantity. Only these count toward the tier, the size mix, the weight card, the freight weight and
 * the totals — the server applies the same rule (QuotationLineRules.IsPostedRow) to the freight it
 * bills, so the screen and the posted Estimate can't disagree.
 *
 * 14ykpqtw3hw: a kg ORDER with complete specs now arrives converted to pieces (QUO-R-003d), so it posts
 * and counts here like any pieces row. A row still in kg is never posted: the submit guard blocks it as
 * unmatched instead (QUO-R-003e).
 */
export function isPostedRow(item: QuotationLineItemDto): boolean {
  return isEditableRow(item) && item.unit === 'pcs' && (item.quantity ?? 0) > 0;
}

/**
 * 14ykpqtr22u — owner rule: cutting lists take pieces only, so rows quantified in kilograms never
 * appear in a quotation (a document listing each bar in pieces AND kilograms used to show — and
 * count — every bar twice). Applied wherever rows enter the client: the extraction result and every
 * load from the server. Returns the rows to keep plus how many kg rows were dropped.
 *
 * 14ykpqtw3hw partially supersedes that for quotations: the server now reads kg against the whole RFQ
 * first. A repeat of a pieces line and a total row are dropped server-side; a kg order is converted to
 * pieces. What still arrives in kg is either a figure without a full size and length (dropped here, as
 * before), or a kg order with no exact catalogue item — KEPT, in kg, so it shows unmatched and blocks
 * submit (owner decision 7). Quotations pass `keep = isUnconvertibleKgOrder` for the second kind. Sales
 * orders call this too (applySalesOrderUnitRule) and pass no `keep`, so their behaviour is unchanged
 * (QUO-R-007).
 */
export function dropKilogramRows<T extends { unit: string }>(
  rows: readonly T[],
  keep?: (row: T) => boolean,
): { rows: T[]; droppedKgLineCount: number } {
  const kept = rows.filter((r) => r.unit !== 'kgs' || (keep?.(r) ?? false));
  return { rows: kept, droppedKgLineCount: rows.length - kept.length };
}

/** The quotation reading of {@link dropKilogramRows}: keep the kg orders the server could not convert. */
export function dropQuotationKilogramRows<
  T extends Pick<QuotationLineItemDto, 'unit' | 'diameterMm' | 'lengthM' | 'gradeCode' | 'originalSpec'>,
>(rows: readonly T[]): { rows: T[]; droppedKgLineCount: number } {
  return dropKilogramRows(rows, isUnconvertibleKgOrder);
}

/**
 * QUO-R-003e — a kg order the server could not convert: complete specs, and marked by the rule's source keys
 * (`originalSpec.sourceUnit`). The mark is what keeps this going forward only: a kg row saved before the rule
 * existed — a pieces twin left on an old AutoDraft — carries no mark and is still dropped on load.
 */
export function isUnconvertibleKgOrder(
  row: Pick<QuotationLineItemDto, 'unit' | 'diameterMm' | 'lengthM' | 'gradeCode' | 'originalSpec'>,
): boolean {
  return row.unit === 'kgs' && isSpecValidForRematch(row) && row.originalSpec?.sourceUnit === 'kgs';
}

/** 14ykpqtr22u — shown where a quotation had only kilogram lines (typically a "per kg price" inquiry). */
export const KG_ONLY_NOTE = 'Only kilogram quantities — nothing to quote as pieces.';

/** 14ykpqtr22u — every line was in kilograms, so nothing is left to quote as pieces. */
export function isKgOnlyDraft(draft: {
  lineItems: readonly unknown[];
  droppedKgLineCount?: number;
}): boolean {
  return draft.lineItems.length === 0 && (draft.droppedKgLineCount ?? 0) > 0;
}

/**
 * The patch that returns a line to its extracted snapshot. Shared by the table and the review list (they
 * each carried a copy). Drops matched-item metadata when the restored spec is incomplete, so the row goes
 * back to "needs match" instead of carrying a stale match against the wrong spec.
 *
 * QUO-R-004 (14ykpqtw3hw): the snapshot's quantity/unit are the NORMALIZED value, so Revert on a rounded or
 * converted line lands on whole pieces — never back on a kg figure the next load would drop.
 */
export function revertPatch(original: LineItemSpecSnapshot): Partial<QuotationLineItemDto> {
  const specsComplete = original.diameterMm != null && original.lengthM != null && original.gradeCode != null;
  return {
    diameterMm: original.diameterMm,
    lengthM: original.lengthM,
    gradeCode: original.gradeCode,
    isWeldable: original.isWeldable,
    quantity: original.quantity,
    unit: original.unit,
    ...(specsComplete
      ? {}
      : {
          matchStatus: 'NEEDS_CLARIFICATION' as const,
          matchedSalesDescription: null,
          matchedItemId: null,
          weightKg: null,
          possibleMatches: [],
        }),
  };
}

// Re-match spec-validity gate (ClickUp 86d3dxnd7).
//
// The /intake/re-match endpoint HARD-validates the spec via
// CSC_CORE/Features/QuotationIntake/Validators/RematchLineItemRequestDtoValidator.cs
// (diameter > 0, length > 0, grade ∈ {33,40,60,75,80}). The client must gate the
// re-match on the SAME rules — gating only on "non-null" lets an invalid spec
// (notably a non-canonical extracted grade) through, firing a request that 400s.
//
// Keep CUTTING_LIST_GRADES in lock-step with the validator's AllowedGrades. 14ykpqtw3hw: this is also the
// QUO-R-001 "complete specs" predicate (CuttingListQuantityRule.CuttingListGrades on the server); the
// parity test scripts/cuttingListQuantityParity.test.ts reads both.
export const CUTTING_LIST_GRADES = [33, 40, 60, 75, 80] as const;
const REMATCH_ALLOWED_GRADES = new Set<number>(CUTTING_LIST_GRADES);

/** True when the line's spec satisfies the server re-match validator, so a
 *  re-match request will not 400. Mirrors RematchLineItemRequestDtoValidator. */
export function isSpecValidForRematch(item: {
  diameterMm?: number | null;
  lengthM?: number | null;
  gradeCode?: string | null;
}): boolean {
  return (
    item.diameterMm != null &&
    item.diameterMm > 0 &&
    item.lengthM != null &&
    item.lengthM > 0 &&
    item.gradeCode != null &&
    REMATCH_ALLOWED_GRADES.has(Number(item.gradeCode))
  );
}
